// Delivery time slots, as minutes from midnight (a plain number is easier to
// compare and round than a "HH:MM" string; the label is derived on render).
//
// The DMS books deliveries on a 30-minute grid: 07:00, 07:30, 08:00 … 20:00
// (DMS-1). Screens narrow that window when they need to — the Contact Center
// splits it into a morning and an afternoon block.

/**
 * Guatemala's offset from UTC. Fixed: the country keeps no daylight saving, so
 * one constant is the whole rule — the backend does the same (`gttime`).
 *
 * Every instant the DMS sends is a Guatemala wall clock. Building it with the
 * browser's own zone would move it by however far the operator's laptop is
 * from Guatemala: on a machine in America/Chicago, an agreed 12:00 reached the
 * backend as 11:00 (DMS-47).
 */
export const GT_UTC_OFFSET = "-06:00";

/** The instant of a Guatemala wall clock, whatever zone the laptop is in. */
export const gtInstant = (isoDate: string, hhmm: string): Date =>
  new Date(`${isoDate}T${hhmm}:00${GT_UTC_OFFSET}`);

/** "YYYY-MM-DD" for a Date's calendar day, as the operator sees it. */
export const calendarDay = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const pad = (n: number): string => String(n).padStart(2, "0");

export const SLOT_MINUTES = 30;

/** First bookable minute of the day (07:00). */
export const DAY_START_MINUTES = 7 * 60;

/** Last bookable minute of the day (20:00). */
export const DAY_END_MINUTES = 20 * 60;

/** "HH:MM" for a minutes-from-midnight slot (450 → "07:30"). */
export const minutesLabel = (minutes: number): string =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** Every 30-minute slot in [from, to], inclusive. Empty when from > to. */
export const slotsEvery30 = (
  from = DAY_START_MINUTES,
  to = DAY_END_MINUTES,
): number[] => {
  const slots: number[] = [];
  for (let m = from; m <= to; m += SLOT_MINUTES) slots.push(m);
  return slots;
};

/** Minutes from midnight for a Date, in local time. */
export const minutesOfDay = (date: Date): number => date.getHours() * 60 + date.getMinutes();

/**
 * The first slot still bookable at `now`, rounding *up* to the 30-minute grid:
 * 11:52 → 12:00, 12:00 → 12:00, 12:01 → 12:30. Used for today's grid, where the
 * ambassador keeps full freedom over the rest of his own day (DMS-1) — every
 * block that has not passed yet is offered, with no extra lead time.
 */
export const nextSlotFrom = (now: Date): number =>
  Math.ceil(minutesOfDay(now) / SLOT_MINUTES) * SLOT_MINUTES;

/** The instant for `day` at the given minutes-from-midnight, read as a
 *  Guatemala wall clock (see GT_UTC_OFFSET). */
export const atSlot = (day: Date, minutes: number): Date =>
  gtInstant(calendarDay(day), minutesLabel(minutes));
